import { Box, Button, Typography } from "@mui/material";
import dayjs from "dayjs";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { EventTypes } from "../../types";

interface Props {
    event: EventTypes.Model,
    onSeeMore: () => void,
    isCarousel ?: boolean
}

const titleMap: Record<string, string> = {
    'report': "Report",
    'alert': "Alert",
    'safetyReport': "Safety Report",
    'userMobileLocation': 'POM User',
    'device': "Device",
};

// Tinted chip palette per event type. Foreground colors mirror the marker
// colors in marker.constants where readable; safetyReport's yellow is darkened
// for legibility on a pale background.
const chipStyleByType: Record<string, { bg: string; fg: string }> = {
    alert: { bg: '#FFE5E5', fg: '#C30000' },
    report: { bg: '#E5E9F4', fg: '#081C63' },
    safetyReport: { bg: '#FFF6D6', fg: '#8A6D00' },
    device: { bg: '#EFE3F2', fg: '#762A83' },
    userMobileLocation: { bg: '#EFE3F2', fg: '#762A83' },
};
const defaultChipStyle = { bg: '#F0F0F0', fg: '#444444' };

export const EventMapPopupContent: React.FC<Props> = ({ event, onSeeMore }) => {
    const chip = chipStyleByType[event?.type] ?? defaultChipStyle;
    const showName = !!event?.userName && event?.type !== 'report' && event?.type !== 'safetyReport';

    return (
        <Box sx={{ minWidth: 250, maxWidth: 300 }}>
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1,
                    py: 0.25,
                    borderRadius: 999,
                    backgroundColor: chip.bg,
                    color: chip.fg,
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: 0.2,
                    textTransform: 'uppercase',
                    mb: showName ? 0.75 : 0.5,
                }}
            >
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: chip.fg }} />
                {titleMap[event?.type] ?? event?.type}
            </Box>

            {showName && (
                <Typography sx={{ fontSize: 16, fontWeight: 600, lineHeight: 1.3, mb: 0.5 }}>
                    {event.userName}
                </Typography>
            )}

            {event?.addressLocation && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.5 }}>
                    <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary', mt: '1px', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 13, color: 'text.primary', lineHeight: 1.35 }}>
                        {event.addressLocation}
                    </Typography>
                </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                    {event?.lastUpdate ? dayjs(event.lastUpdate).format('MMM D, YYYY h:mm A') : 'N/A'}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                    variant="text"
                    size="small"
                    onClick={onSeeMore}
                    sx={{ fontSize: 13, fontWeight: 600, textTransform: 'none', px: 1, minWidth: 0 }}
                >
                    View details →
                </Button>
            </Box>
        </Box>
    );
};
